<script>
/**
 * @component ProcedureTypeAdmin
 * @description UC-REG-160 — ABM de tipos de trámite. Admin/manager configura
 *              qué tipos aparecen en el select del form del paciente/prestador
 *              cuando envía un nuevo pedido.
 *
 *              Multi-tenant: cada organización tiene su catálogo propio.
 *              Sin categorías/roles (v2 si aparece necesidad).
 *
 *              Delete = soft (isActive=false) — preserva referencias en
 *              Procedures ya emitidos.
 *
 * @example <ProcedureTypeAdmin />
 */
import { procedureTypeService } from "@/services/procedure";

export default {
  name: "ProcedureTypeAdmin",

  data() {
    return {
      types: [],
      loading: false,
      modal: {
        visible: false,
        mode: "create", // 'create' | 'edit'
        form: { _id: null, name: "", description: "", isActive: true },
        saving: false,
      },
    };
  },

  created() {
    this.load();
  },

  methods: {
    /**
     * @description Trae todos los tipos (activos + inactivos) para el ABM.
     */
    async load() {
      this.loading = true;
      try {
        this.types = await procedureTypeService.list({
          includeInactive: "true",
        });
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "No se pudo cargar el catálogo"
        );
      } finally {
        this.loading = false;
      }
    },

    openCreate() {
      this.modal = {
        visible: true,
        mode: "create",
        form: { _id: null, name: "", description: "", isActive: true },
        saving: false,
      };
    },

    openEdit(type) {
      this.modal = {
        visible: true,
        mode: "edit",
        form: {
          _id: type._id,
          name: type.name || "",
          description: type.description || "",
          isActive: type.isActive !== false,
        },
        saving: false,
      };
    },

    close() {
      this.modal.visible = false;
    },

    /**
     * @description Crea o edita según el modo del modal.
     */
    async save() {
      const { form, mode } = this.modal;
      if (!form.name.trim()) {
        this.$message.warning("El nombre es obligatorio");
        return;
      }
      this.modal.saving = true;
      try {
        const payload = {
          name: form.name.trim(),
          description: form.description.trim(),
          isActive: form.isActive,
        };
        if (mode === "create") {
          await procedureTypeService.create(payload);
        } else {
          await procedureTypeService.update(form._id, payload);
        }
        this.close();
        await this.load();
        this.$message.success("Guardado");
      } catch (err) {
        this.$message.error(err?.response?.data?.error || "No se pudo guardar");
      } finally {
        this.modal.saving = false;
      }
    },

    /**
     * @description Confirma con el user antes de soft-delete.
     */
    async remove(type) {
      const ok = await this.$confirm(
        `¿Dar de baja el tipo "${type.name}"? Los trámites ya emitidos seguirán refiriendo a este tipo, pero el paciente no lo verá al crear nuevos.`,
        "Baja de tipo",
        {
          confirmButtonText: "Dar de baja",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      ).catch(() => false);
      if (!ok) return;
      try {
        await procedureTypeService.remove(type._id);
        await this.load();
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error || "No se pudo dar de baja"
        );
      }
    },
  },
};
</script>

<template lang="pug">
.proc-admin
  .proc-admin__header
    h2.proc-admin__title Tipos de trámite
    button.proc-admin__new(@click="openCreate")
      | + Nuevo tipo
  p.proc-admin__hint Los tipos que estén activos aparecen en el formulario del paciente/prestador al enviar un trámite.

  .proc-admin__empty(v-if="!loading && types.length === 0")
    p Todavía no cargaste ningún tipo.
    p Creá al menos uno para que los pacientes/prestadores puedan enviar trámites.

  table.proc-admin__table(v-if="types.length > 0")
    thead
      tr
        th Nombre
        th Descripción
        th Estado
        th.proc-admin__actions-col Acciones
    tbody
      tr(v-for="t in types" :key="t._id")
        td
          strong {{ t.name }}
        td.proc-admin__desc {{ t.description || "—" }}
        td
          span.proc-admin__badge(:class="{ 'is-active': t.isActive, 'is-inactive': !t.isActive }")
            | {{ t.isActive ? "Activo" : "Inactivo" }}
        td.proc-admin__actions
          button.proc-admin__link(@click="openEdit(t)") Editar
          button.proc-admin__link.proc-admin__link--danger(v-if="t.isActive" @click="remove(t)") Dar de baja

  //- Modal ABM
  .proc-admin__modal-backdrop(v-if="modal.visible" @click.self="close")
    .proc-admin__modal
      h3 {{ modal.mode === "create" ? "Nuevo tipo de trámite" : "Editar tipo" }}
      .proc-admin__field
        label Nombre *
        input(v-model="modal.form.name" type="text" maxlength="80" placeholder="Ej: Solicitud de historia clínica")
      .proc-admin__field
        label Descripción (opcional)
        textarea(v-model="modal.form.description" rows="2" maxlength="240" placeholder="Aparece bajo el nombre en el formulario")
      .proc-admin__field.proc-admin__field--inline
        input(type="checkbox" v-model="modal.form.isActive" id="isActive")
        label(for="isActive") Activo (visible en el formulario del paciente)
      .proc-admin__modal-actions
        button.proc-admin__btn.proc-admin__btn--secondary(@click="close" :disabled="modal.saving") Cancelar
        button.proc-admin__btn.proc-admin__btn--primary(@click="save" :disabled="modal.saving")
          | {{ modal.saving ? "Guardando…" : "Guardar" }}
</template>

<style lang="scss" scoped>
.proc-admin {
  padding: 20px;
}
.proc-admin__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.proc-admin__title {
  margin: 0;
  font-size: 18px;
  color: #0f172a;
}
.proc-admin__new {
  background: $teal;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    background: darken($teal, 5%);
  }
}
.proc-admin__hint {
  font-size: 12px;
  color: #64748b;
  margin: 0 0 20px 0;
}
.proc-admin__empty {
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
  font-size: 13px;
  border: 1px dashed #e2e8f0;
  border-radius: 10px;
  p {
    margin: 4px 0;
  }
}
.proc-admin__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  thead th {
    text-align: left;
    padding: 10px 12px;
    color: #64748b;
    font-weight: 700;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid #e2e8f0;
  }
  tbody td {
    padding: 12px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: top;
  }
  tbody tr:hover {
    background: #f8fafc;
  }
}
.proc-admin__desc {
  color: #64748b;
  max-width: 400px;
}
.proc-admin__actions-col {
  width: 200px;
}
.proc-admin__actions {
  text-align: right;
}
.proc-admin__badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  &.is-active {
    background: rgba(22, 163, 74, 0.12);
    color: #16a34a;
  }
  &.is-inactive {
    background: #f1f5f9;
    color: #64748b;
  }
}
.proc-admin__link {
  background: none;
  border: none;
  color: $teal;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  padding: 4px 8px;
  &:hover {
    text-decoration: underline;
  }
  &--danger {
    color: #dc2626;
  }
}
.proc-admin__modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.proc-admin__modal {
  background: white;
  border-radius: 12px;
  padding: 24px;
  min-width: 480px;
  max-width: 90vw;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  h3 {
    margin: 0 0 20px 0;
    font-size: 16px;
    color: #0f172a;
  }
}
.proc-admin__field {
  margin-bottom: 16px;
  label {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #334155;
    margin-bottom: 6px;
  }
  input[type="text"],
  textarea {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 13px;
    font-family: inherit;
    &:focus {
      outline: none;
      border-color: $teal;
    }
  }
  &--inline {
    display: flex;
    align-items: center;
    gap: 8px;
    input[type="checkbox"] {
      margin: 0;
    }
    label {
      margin: 0;
    }
  }
}
.proc-admin__modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
}
.proc-admin__btn {
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &--primary {
    background: $teal;
    color: white;
    &:hover:not(:disabled) {
      background: darken($teal, 5%);
    }
  }
  &--secondary {
    background: #f1f5f9;
    color: #334155;
    &:hover:not(:disabled) {
      background: #e2e8f0;
    }
  }
}
</style>
